import { useCallback } from 'react';
import { zWeightUnit } from '@strength/domain';
import { getPrescribableUnits } from '@strength/domain-workout';
import { DefaultWeightUnit } from '@strength/db-schema';
import { trpc } from '../../trpc/react';
import { RouterOutput } from '../../trpc';
import { useMetrics } from './useMetrics';

/** A per-team default weight unit row from the multi-team tablet endpoint. */
type WeightUnitRow = RouterOutput['tablet']['weightUnits'][number];

/** Resolves a team's default weight unit; `'lbs'` when not loaded or team absent. */
export function resolveDefaultWeightUnit(
  rows: readonly WeightUnitRow[],
  teamId: string,
): DefaultWeightUnit {
  return rows.find((r) => r.teamId === teamId)?.unit ?? 'lbs';
}

/**
 * Multi-team variant of `useMetrics` for the tablet collection screen: resolves
 * the default weight unit PER team so each athlete uses their own team's unit.
 * Metrics are org-scoped (shared with `useMetrics`); only the default unit is
 * team-specific. Empty `teamIds` → no request fires and accessors fall back to `'lbs'`.
 */
export function useTabletMetrics(teamIds: string[]) {
  const enabled = teamIds.length > 0;
  // Reused only for the org-scoped metrics library; on the tablet collection
  // screen the viewer team is the base library so `useMetrics`'s single-team
  // weight-unit query stays disabled. The unit here is resolved per team below.
  const { getMetric } = useMetrics();

  // Header-only endpoint (scope comes from `tw-team-ids`, not input); `teamIds`
  // gates the fetch and resolves the unit per athlete's team below.
  const query = trpc.tablet.weightUnits.useQuery(undefined, {
    staleTime: Infinity,
    enabled,
  });

  const getDefaultWeightUnit = useCallback(
    (teamId: string): DefaultWeightUnit =>
      resolveDefaultWeightUnit(query.data ?? [], teamId),
    [query.data],
  );

  // Mirrors `useMetrics.getUnitsForMetric` but keyed on the per-team default unit.
  const getUnitsForMetric = useCallback(
    (id: string, teamId: string, forActuals = false) => {
      const metric = getMetric(id);
      if (metric.type === 'intensity') {
        const defaultWeightUnit = getDefaultWeightUnit(teamId);
        const weightUnits = [
          defaultWeightUnit,
          ...zWeightUnit.options.filter((unit) => unit !== defaultWeightUnit),
        ];
        // Mirrors useMetrics.getUnitsForMetric: intensity has always accepted
        // a typed '%', so offer it when prescribing (appended, not prepended,
        // so the default unit stays first).
        return forActuals ? weightUnits : [...weightUnits, '%'];
      }

      return getPrescribableUnits(metric.config, forActuals);
    },
    [getDefaultWeightUnit, getMetric],
  );

  const { refetch } = query;
  const rehydrate = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    getDefaultWeightUnit,
    getUnitsForMetric,
    isLoading: query.isLoading,
    error: query.error?.message,
    rehydrate,
  };
}
